/**
 * The transfers strip, as data: which rows belong to one folder upload, what
 * that folder's totals are, and the strip's overall progress.
 *
 * Pure so the numbers the user reads ("3 of 12 files · 4.2 MB / 12.4 MB") are
 * tested rather than eyeballed. A row's status is derived in ONE place
 * (`transferStatus`) so the row, the group and the header can never disagree.
 */

/** 'queued' | 'running' | 'done' | 'replaced' | 'failed' | 'canceled' | 'skipped' */
export function transferStatus(tr) {
  if (tr.skipped) return 'skipped';
  if (tr.error) return 'failed';
  if (tr.canceled) return 'canceled';
  if (tr.done) return tr.replaced ? 'replaced' : 'done';
  if (tr.queued) return 'queued';
  return 'running';
}

export const isFinished = (tr) => !!tr.done || !!tr.canceled || !!tr.skipped;

const num = (v) => Number(v) || 0;

/**
 * Totals over a set of rows. Skipped rows are counted apart: they were never
 * going to move, so "3 of 12 files" must not include them in the 12.
 */
export function summarize(rows) {
  let files = 0;
  let finishedOk = 0;
  let failed = 0;
  let canceled = 0;
  let skipped = 0;
  let active = 0;
  let total = 0;
  let transferred = 0;
  for (const tr of rows || []) {
    const status = transferStatus(tr);
    if (status === 'skipped') {
      skipped += 1;
      continue;
    }
    files += 1;
    if (status === 'done' || status === 'replaced') finishedOk += 1;
    else if (status === 'failed') failed += 1;
    else if (status === 'canceled') canceled += 1;
    else active += 1;
    total += num(tr.total);
    // A finished row counts as complete even if the last progress event never
    // arrived; a failed one counts what it actually moved.
    transferred += status === 'done' || status === 'replaced' ? num(tr.total) || num(tr.transferred) : num(tr.transferred);
  }
  const percent = total > 0 ? Math.min(100, Math.round((transferred / total) * 100)) : null;
  return { files, finishedOk, failed, canceled, skipped, active, total, transferred, percent };
}

/**
 * Rows → display items, in arrival order. A folder upload's rows collapse into
 * ONE group item at the position of its first row, so a 300-file upload is one
 * line with a toggle rather than 300 lines pushing everything else away.
 *
 * @returns {Array<{type: 'row', tr: object} | {type: 'group', id: string, name: string, rows: object[], summary: object}>}
 */
export function groupTransfers(transfers) {
  const items = [];
  const groups = new Map();
  for (const tr of transfers || []) {
    if (!tr.groupId) {
      items.push({ type: 'row', tr });
      continue;
    }
    let group = groups.get(tr.groupId);
    if (!group) {
      group = { type: 'group', id: tr.groupId, name: tr.groupName || '', path: tr.groupPath || '', rows: [] };
      groups.set(tr.groupId, group);
      items.push(group);
    }
    group.rows.push(tr);
  }
  for (const group of groups.values()) group.summary = summarize(group.rows);
  return items;
}

/** Header numbers: how many are still moving, and how far along they are. */
export function overallProgress(transfers) {
  const live = (transfers || []).filter((tr) => !isFinished(tr));
  const s = summarize(live);
  return { active: live.length, percent: s.percent };
}

/** "4.2 MB / 12.4 MB" while moving, the final size once done, nothing when unknown. */
export function sizeLabel(tr, formatBytes) {
  const status = transferStatus(tr);
  const total = num(tr.total);
  const moved = num(tr.transferred);
  if (status === 'done' || status === 'replaced') return total > 0 ? formatBytes(total) : moved > 0 ? formatBytes(moved) : '';
  if (status === 'skipped' || status === 'queued') return total > 0 ? formatBytes(total) : '';
  if (total > 0) return `${formatBytes(moved)} / ${formatBytes(total)}`;
  return moved > 0 ? formatBytes(moved) : '';
}
